import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { Container, Card, CardHeader, CardContent, TextField, Stack, Alert } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers';

import { getUsers } from '../../users/store/userActions';
import { fDateYMD } from '../../../utils/formatTime';
import { getAuditBranches } from '../../branches/store/branchActions';
import { createAuditRecord } from '../store/auditActions';
import { Page } from '../../../components';
import { FormProvider, RHFSelect, RHFTextField } from '../../../components/hook-form';

const NewAuditSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  description: Yup.string(),
  staffId: Yup.string(),
  branchId: Yup.string().required('Location is required'),
});

export default function AuditForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { auditBranches } = useSelector((state) => state.branchReducer);
  const { users } = useSelector((state) => state.userReducer);

  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(null);

  const methods = useForm({
    resolver: yupResolver(NewAuditSchema),
    defaultValues: { name: '', description: '', staffId: '', branchId: '' },
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    dispatch(getAuditBranches({}));
    dispatch(getUsers({}));
  }, [dispatch]);

  // Awaited, unlike the original, which reset the form and navigated away
  // immediately — announcing success before the request had been answered, so
  // a rejected audit still said "Audit created successfully".
  const onSubmit = async (data) => {
    const result = await dispatch(
      createAuditRecord({
        location_id: data.branchId,
        staff_id: data.staffId || null,
        name: data.name,
        description: data.description,
        start_date: startDate ? fDateYMD(startDate) : null,
        end_date: endDate ? fDateYMD(endDate) : null,
      })
    );
    if (result.ok) {
      reset();
      // Straight to the checklist that was just built, which is where whoever
      // created the audit is going next anyway.
      navigate(result.data?.id ? `/dashboard/audits/${result.data.id}` : '/dashboard/audits');
    }
  };

  return (
    <Page title="New Asset Audit">
      <Container maxWidth="xl">
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Card sx={{ p: 3 }}>
            <CardHeader title="Create New Audit" />
            <CardContent>
              <Stack spacing={2}>
                <Alert severity="info">
                  Creating the audit takes a snapshot of every asset the register currently holds at the location. That
                  checklist is what the count is measured against, so anything transferred afterwards still shows up.
                </Alert>

                <RHFTextField name="name" label="Audit Name" />
                <RHFTextField name="description" label="Description" multiline rows={2} />

                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  minDate={startDate ?? undefined}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />

                <RHFSelect name="branchId" label="Location *" placeholder="Location">
                  <option value="" />
                  {auditBranches?.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </RHFSelect>

                <RHFSelect name="staffId" label="Assign to" placeholder="Assign">
                  <option value="" />
                  {/* `users/` returns Staff rows, so the id here is already a
                      staff id — which is what the API's staff_id expects. */}
                  {users?.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.user?.display_name || option.user?.email}
                    </option>
                  ))}
                </RHFSelect>

                <Stack alignItems="flex-end" sx={{ mt: 3 }}>
                  <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
                    Create Audit
                  </LoadingButton>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </FormProvider>
      </Container>
    </Page>
  );
}
